'use client';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/shadcn/dialog';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { toast } from '@/components/ui/Toasts/use-toast';
import { Interview } from '@/types/pulsedb';
import { Workspace } from '@/types/pulse';
import { updateInterview } from '@/utils/supabase/actions';
import { useWorkspaces } from '@/utils/clerk/client';
import { Fragment, useState } from 'react';
import { UserIcon } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/radio-group';

export default function InterviewMoveDialog({
  interview,
  onMoved,
  moveDialogOpen,
  setMoveDialogOpen
}: {
  interview: Interview;
  onMoved: (workspace: Workspace) => void;
  moveDialogOpen: boolean;
  setMoveDialogOpen: (open: boolean) => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { workspaces } = useWorkspaces();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');

  const availableWorkspaces =
    workspaces
      ?.filter((workspace) => workspace.id !== interview.workspace_id)
      .sort((a, b) => {
        // Personal workspaces first, then alphabetical by name
        if (a.personal && !b.personal) return -1;
        if (!a.personal && b.personal) return 1;
        return a.name.localeCompare(b.name);
      }) || [];

  const moveInterviewHandler = async () => {
    if (!selectedWorkspaceId) return;

    const selectedWorkspace = availableWorkspaces.find(
      (workspace) => workspace.id === selectedWorkspaceId
    );
    if (!selectedWorkspace) return;

    setIsSubmitting(true);
    try {
      await updateInterview(interview.id, {
        workspace_id: selectedWorkspace.id
      });
      toast({
        title: 'Interview moved successfully'
      });
      onMoved(selectedWorkspace);
      setMoveDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Failed to move interview',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDialogHandler = () => {
    setMoveDialogOpen(false);
  };

  return (
    <Dialog
      open={moveDialogOpen}
      onOpenChange={(open) => {
        if (!open) setMoveDialogOpen(false);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move Interview</DialogTitle>
        </DialogHeader>
        {availableWorkspaces.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No other workspaces available to move this interview to.
          </p>
        ) : (
          <Field label="Select a workspace to move this interview to:">
            <div className="max-h-64 w-full overflow-y-auto">
              <RadioGroup
                value={selectedWorkspaceId}
                onValueChange={setSelectedWorkspaceId}
                className="space-y-3"
              >
                {availableWorkspaces.map((workspace) => (
                  <Fragment key={workspace.id}>
                    <hr className="border-border" />
                    <div className="flex items-center gap-3 pr-2">
                      <RadioGroupItem
                        value={workspace.id}
                        id={`ws-${workspace.id}`}
                      />
                      <label
                        htmlFor={`ws-${workspace.id}`}
                        className="flex-1 cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          {workspace.personal && (
                            <UserIcon className="text-zinc-700" size={16} />
                          )}
                          <span className="text-sm font-medium">
                            {workspace.name}
                          </span>
                          {workspace.personal && (
                            <span className="ml-auto text-xs text-muted-foreground">
                              Personal
                            </span>
                          )}
                        </div>
                      </label>
                    </div>
                  </Fragment>
                ))}
              </RadioGroup>
            </div>
          </Field>
        )}
        <DialogFooter>
          <Button
            onClick={closeDialogHandler}
            disabled={isSubmitting}
            variant="outline"
            size="sm"
          >
            Cancel
          </Button>
          <Button
            onClick={moveInterviewHandler}
            disabled={isSubmitting || !selectedWorkspaceId}
            variant="primary"
            size="sm"
          >
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
